import * as React from 'react'
import { Loader2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useToast } from '@/components/ui/use-toast'
import { apiJson, apiFetch } from '@/lib/api'
import type { AssetFolder, FolderType } from '@/types'

interface FolderManagerProps {
  mode: 'create' | 'edit' | 'delete'
  folder?: AssetFolder
  folderType?: FolderType
  open: boolean
  onOpenChange: (open: boolean) => void
  onSuccess: () => void
}

export function FolderManager({
  mode,
  folder,
  folderType = 'sku',
  open,
  onOpenChange,
  onSuccess,
}: FolderManagerProps) {
  const { toast } = useToast()
  const [name, setName] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [submitting, setSubmitting] = React.useState(false)

  React.useEffect(() => {
    if (open) {
      setName(folder?.name || '')
      setDescription(folder?.description || '')
    }
  }, [open, folder])

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      if (mode === 'create') {
        await apiJson('/api/v1/folders', {
          method: 'POST',
          body: JSON.stringify({
            name: name.trim(),
            folder_type: folderType,
            description: description.trim() || null,
          }),
        })
        toast({ title: 'Folder created', description: name })
      } else if (mode === 'edit' && folder) {
        await apiJson(`/api/v1/folders/${folder.id}`, {
          method: 'PUT',
          body: JSON.stringify({
            name: name.trim(),
            description: description.trim() || null,
          }),
        })
        toast({ title: 'Folder updated', description: name })
      } else if (mode === 'delete' && folder) {
        const res = await apiFetch(`/api/v1/folders/${folder.id}`, {
          method: 'DELETE',
        })
        if (!res.ok) {
          const data = await res.json().catch(() => ({}))
          throw new Error(data.detail || 'Delete failed')
        }
        toast({ title: 'Folder deleted', description: folder.name })
      }
      onOpenChange(false)
      onSuccess()
    } catch (err) {
      toast({
        title: 'Error',
        description: (err as Error).message,
        variant: 'destructive',
      })
    } finally {
      setSubmitting(false)
    }
  }

  const titles: Record<string, string> = {
    create: 'Create folder',
    edit: 'Rename folder',
    delete: 'Delete folder',
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[400px]">
        <DialogHeader>
          <DialogTitle>{titles[mode]}</DialogTitle>
          {mode === 'delete' && (
            <DialogDescription>
              This will permanently delete &quot;{folder?.name}&quot; and all{' '}
              {folder?.asset_count || 0} asset(s) inside it.
            </DialogDescription>
          )}
        </DialogHeader>

        {mode !== 'delete' && (
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Folder name</Label>
              <Input
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Skincare Serum SKU"
                autoFocus
                disabled={submitting}
              />
            </div>
            {mode === 'create' && (
              <div className="space-y-2">
                <Label>Description (optional)</Label>
                <Input
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Product line, campaign, etc."
                  disabled={submitting}
                />
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={submitting}
          >
            Cancel
          </Button>
          <Button
            variant={mode === 'delete' ? 'destructive' : 'default'}
            onClick={handleSubmit}
            disabled={submitting || (mode !== 'delete' && !name.trim())}
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            {mode === 'delete' ? 'Delete' : mode === 'create' ? 'Create' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
